/**
 * Tayro logo celebration — "the runner runs the trail, jumps at the top".
 * E2-b, from the E1 motion spec (design handoff motion/logo-celebration.md,
 * reference b1-motion.jsx). The ONLY sanctioned brand animation.
 *
 * Plays ONCE per mount (~2.4s) and settles into the exact static TayroMark
 * geometry; never loops. Renders the static mark instead when
 * prefers-reduced-motion is set (or matchMedia is unavailable — SSR
 * fail-safe). Decorative (aria-hidden), like the static mark without a
 * title.
 */
import { useEffect, useRef, useState } from 'react';
import { TayroMark } from './TayroMark';

const PTS: ReadonlyArray<readonly [number, number]> = [
  [15, 46],
  [27, 26],
  [35, 34],
  [46, 20],
];

const SEGS = (() => {
  const segs: { from: readonly [number, number]; to: readonly [number, number]; len: number; start: number }[] = [];
  let total = 0;
  for (let i = 1; i < PTS.length; i++) {
    const dx = PTS[i][0] - PTS[i - 1][0];
    const dy = PTS[i][1] - PTS[i - 1][1];
    const len = Math.hypot(dx, dy);
    segs.push({ from: PTS[i - 1], to: PTS[i], len, start: total });
    total += len;
  }
  return { segs, total };
})();

function polyPoint(p: number): readonly [number, number] {
  const d = Math.min(Math.max(p, 0), 1) * SEGS.total;
  for (const s of SEGS.segs) {
    if (d <= s.start + s.len || s === SEGS.segs[SEGS.segs.length - 1]) {
      const q = Math.min((d - s.start) / s.len, 1);
      return [s.from[0] + (s.to[0] - s.from[0]) * q, s.from[1] + (s.to[1] - s.from[1]) * q];
    }
  }
  return PTS[PTS.length - 1];
}

/** Jump arc — quadratic bezier from trail top (46,20) over the apex to the
 *  resting point (50.5,14.5). Geometry per the motion spec. */
function jumpPoint(t: number): readonly [number, number] {
  const P0 = [46, 20];
  const C = [47.5, 4.5];
  const P1 = [50.5, 14.5];
  const u = 1 - t;
  return [
    u * u * P0[0] + 2 * u * t * C[0] + t * t * P1[0],
    u * u * P0[1] + 2 * u * t * C[1] + t * t * P1[1],
  ];
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
// JS analogue of --ease-celebrate (cubic-bezier(.34,1.56,.64,1)) for the settle spring.
const easeOutBack = (t: number) => {
  const c = 1.70158;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
};

// Timeline (ms) per the motion spec table. The run is a narrative beat with
// no token; pulse + settle mirror --dur-celebrate (520ms, tokens.css) — keep
// T_PULSE in sync with the token.
const T_DELAY = 300;
const T_RUN = 1300;
const T_CROUCH = 140;
const T_JUMP = 420;
const T_SETTLE = 320;
const T_PULSE = 520; // = --dur-celebrate
export const CELEBRATION_TOTAL_MS = T_DELAY + T_RUN + T_CROUCH + T_JUMP + Math.max(T_SETTLE, T_PULSE);

export type CelebrationFrame = {
  pos: readonly [number, number];
  sx: number;
  sy: number;
  trail: number;
  pulse: { r: number; o: number } | null;
};

/** Pure timeline → frame. Exported for tests: the end state must equal the
 *  static mark exactly (dot at rest, full trail, no scale, no pulse). */
export function celebrationFrame(ms: number): CelebrationFrame {
  const top: readonly [number, number] = [46, 20];
  const rest: readonly [number, number] = [50.5, 14.5];
  if (ms < T_DELAY) return { pos: PTS[0], sx: 1, sy: 1, trail: 0, pulse: null };
  let t = ms - T_DELAY;
  if (t < T_RUN) {
    const p = easeInOutCubic(t / T_RUN);
    return { pos: polyPoint(p), sx: 1, sy: 1, trail: p, pulse: null };
  }
  t -= T_RUN;
  if (t < T_CROUCH) {
    // Anticipation squash, anchored to the ground (bottom edge of the dot).
    const p = t / T_CROUCH;
    const sy = 1 - 0.28 * Math.sin(p * Math.PI);
    const sx = 1 + 0.18 * Math.sin(p * Math.PI);
    return { pos: top, sx, sy, trail: 1, pulse: null };
  }
  t -= T_CROUCH;
  if (t < T_JUMP) {
    const p = t / T_JUMP;
    const stretch = Math.sin(p * Math.PI);
    return { pos: jumpPoint(p), sx: 1 - 0.12 * stretch, sy: 1 + 0.18 * stretch, trail: 1, pulse: null };
  }
  t -= T_JUMP;
  // Landing: squash → spring back; one pulse ring expands and fades.
  const sp = Math.min(t / T_SETTLE, 1);
  const k = easeOutBack(sp);
  const sy = 0.62 + (1 - 0.62) * k;
  const sx = 1.3 - 0.3 * k;
  const pp = Math.min(t / T_PULSE, 1);
  const pulse = pp < 1 ? { r: 5 + 11 * pp, o: 0.85 * (1 - pp) } : null;
  return { pos: rest, sx, sy, trail: 1, pulse };
}

type TayroMarkCelebrationProps = {
  /** Rendered size in px (regular cut only — celebrations are ≥24px). */
  size?: number;
  /** Fires once when the timeline completes (or immediately when static). */
  onDone?: () => void;
};

export function TayroMarkCelebration({ size = 64, onDone }: TayroMarkCelebrationProps): JSX.Element {
  const prefersStatic =
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [ms, setMs] = useState(prefersStatic ? CELEBRATION_TOTAL_MS : 0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (prefersStatic) {
      onDoneRef.current?.();
      return;
    }
    let raf: number;
    const t0 = performance.now();
    const tick = (now: number) => {
      const el = now - t0;
      setMs(el);
      if (el < CELEBRATION_TOTAL_MS) {
        raf = requestAnimationFrame(tick);
      } else {
        onDoneRef.current?.();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Once per mount — the timeline never restarts or loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (prefersStatic) return <TayroMark size={size} />;

  const f = celebrationFrame(ms);
  const dotR = 4;
  // Squash anchors to the dot's bottom edge so the crouch reads as
  // compressing into the ground, not shrinking in place.
  const cy = f.pos[1] + dotR * (1 - f.sy);

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="16" fill="var(--signal)" />
      <path
        d="M15 46 L27 26 L35 34 L46 20"
        fill="none"
        stroke="var(--paper)"
        strokeWidth={6.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={SEGS.total}
        strokeDashoffset={SEGS.total * (1 - f.trail)}
      />
      {f.pulse !== null && (
        <circle
          cx={f.pos[0]}
          cy={f.pos[1]}
          r={f.pulse.r}
          fill="none"
          stroke="var(--today)"
          strokeWidth={1.6}
          opacity={f.pulse.o}
        />
      )}
      <g transform={`translate(${f.pos[0]}, ${cy}) scale(${f.sx}, ${f.sy})`}>
        <circle cx="0" cy="0" r={dotR} fill="var(--today)" />
      </g>
    </svg>
  );
}
